/**
 * @fileoverview Input/output schemas for the iterative-refinement-v2 agent.
 *
 * This composite agent runs a critique-judge-refine loop:
 * 1. critique-committee-v2: Runs all 3 critics in parallel
 * 2. draft-judge-v2: Evaluates if the draft is satisfactory
 * 3. run-refine-full-v2: Synthesizes findings and refines the draft
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

/**
 * Minimal input from UI/hooks - just what the user provides.
 */
export const HookInputSchema = z.object({
  chapterId: z.string().describe('The ID of the chapter to refine'),
  initialContent: z.string().describe('The starting content to refine'),
  userMessage: z.string().optional().describe('Optional user guidance for the refinement'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow - includes all context needed for all steps.
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.styleGuide.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.seriesContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.povCharacter.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  /** The starting content to refine */
  initialContent: z.string().describe('The starting content of the chapter to be refined'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output includes the final refined content plus iteration metadata.
 */
export const OutputSchema = z.object({
  content: z.string().describe(
    'The final refined chapter content, formatted as a single HTML string.'
  ),
  commentary: z.string().describe(
    'Combined commentary summarizing the refinement process and final state.'
  ),
  /** Why the loop exited */
  exitReason: z.enum(['satisfied', 'max_iterations']).describe(
    'Why the refinement loop ended: satisfied (draft-judge approved) or max_iterations (hit limit)'
  ),
  /** Number of iterations completed */
  iterationsCompleted: z.number().describe('Number of critique-judge-refine cycles completed'),
  /** Details from each iteration */
  iterations: z.array(z.object({
    judgeCommentary: z.string().describe('Commentary from draft-judge-v2 for this iteration'),
    judgeSatisfied: z.boolean().describe('Whether draft-judge-v2 was satisfied'),
    topPriorities: z.array(z.string()).optional().describe('Priorities from draft-judge-v2 if not satisfied'),
    refinerCommentary: z.string().optional().describe('Commentary from run-refine-full-v2 if run'),
    durationMs: z.number().describe('Duration of this iteration in milliseconds'),
  })).optional().describe('Detailed results from each iteration'),
});

export type Output = z.infer<typeof OutputSchema>;
